import type { Email, EmailTemplate } from '../content/emails';
import type { EventBus, PendingPayment } from './EventBus';
import { PAYMENT_ELIGIBLE_CUSTOMERS, openInvoicesForCustomer, buildRemittanceHint } from '../content/customer-pool';

/** Local mirror of PAYMENTS_WINDOW.expirationMs to avoid pulling Phaser into
 *  the procedural-events module (the gameConfig module imports phaser). */
const PAYMENT_EXPIRATION_MS = 180_000;

const BUCKETS = ['0-30', '31-60', '61-90', '90+'] as const;
type Bucket = (typeof BUCKETS)[number];

const PROCEDURAL_AGING_CUSTOMERS = [
  'Acme Corp',
  'Bluebird Foods',
  'Cypress Logistics',
  'Forest Wholesale',
  'Granite Partners',
  'Ivory Market',
];

const PROCEDURAL_CALL_CALLERS = [
  'unknown caller',
  'Bluebird Foods AP',
  'Granite Partners AP',
  'Atlas Trading AP',
];

export interface ProceduralEventsOptions {
  bus: EventBus;
  getNowMs: () => number;
  pool: EmailTemplate[];
  rng: () => number;
  /** Base interval at game start (ms). Ramps shorter as the day progresses. */
  intervalMs: number;
  formatTimeLabel?: (nowMs: number) => string;
  /** Total real-time runtime in ms — used to compute pacing-ramp scalar. */
  runtimeMs?: number;
  /** When true, scripted gating is bypassed (e.g. endless mode). */
  unrestricted?: boolean;
  /** Per-kind gates. */
  agingUnlocked?: () => boolean;
  paymentsUnlocked?: () => boolean;
  callsUnlocked?: () => boolean;
}

type EventKind = 'email' | 'aging-tick' | 'call' | 'payment';

const BASE_SHARES: Record<EventKind, number> = {
  // Spec §7: 50% email, 25% aging-tick, 15% payment, 10% call.
  email: 0.50,
  'aging-tick': 0.25,
  payment: 0.15,
  call: 0.10,
};

/** Per-kind multiplier (0 = off, 1 = normal). The settings modal pushes
 *  updates here; procedural picks are weighted by base × multiplier. */
export interface ProceduralRates {
  email: number;
  'aging-tick': number;
  payment: number;
  call: number;
  /** Master pause — when true, no procedural events fire. */
  paused: boolean;
}

const DEFAULT_RATES: ProceduralRates = {
  email: 1,
  'aging-tick': 1,
  payment: 1,
  call: 1,
  paused: false,
};

export class ProceduralEvents {
  private started = false;
  private rates: ProceduralRates = { ...DEFAULT_RATES };
  private startedAtMs = 0;
  private nextSpawnAtMs = 0;
  private idCounter = 0;
  /** Within-burst countdown: number of events still queued in the current burst. */
  private burstRemaining = 0;

  constructor(private readonly opts: ProceduralEventsOptions) {}

  start(): void {
    if (this.started) return;
    this.started = true;
    this.startedAtMs = this.opts.getNowMs();
    // First spawn lands ~3s in.
    this.nextSpawnAtMs = this.startedAtMs + 3000;
  }

  stop(): void {
    this.started = false;
  }

  setRates(next: Partial<ProceduralRates>): void {
    this.rates = { ...this.rates, ...next };
  }

  getRates(): ProceduralRates {
    return { ...this.rates };
  }

  tick(): void {
    if (!this.started) return;
    if (this.rates.paused) return;
    const now = this.opts.getNowMs();
    while (now >= this.nextSpawnAtMs) {
      this.spawn(this.nextSpawnAtMs);
      this.scheduleNext();
    }
  }

  /** Spec §7 pacing ramp: 9-11 baseline, 11-14 moderate, 14-16 high, 16-17 peak.
   *  Returns a multiplier on intervalMs. Starts at 1.0, descends to ~0.5. */
  private currentRampScalar(): number {
    const runtime = this.opts.runtimeMs ?? 22 * 60 * 1000;
    const elapsed = Math.max(0, this.opts.getNowMs() - this.startedAtMs);
    const fraction = Math.min(1, elapsed / runtime);
    // 0.0-0.227 (9-11): 1.0
    // 0.227-0.591 (11-14): 0.8
    // 0.591-0.864 (14-16): 0.6
    // 0.864-1.0 (16-17): 0.5
    if (fraction < 0.227) return 1.0;
    if (fraction < 0.591) return 0.8;
    if (fraction < 0.864) return 0.6;
    return 0.5;
  }

  /** Schedule the next spawn — burst-aware. Within a burst, gap is ~1.5-3s.
   *  Between bursts, gap is intervalMs * scalar (15-30s typical). */
  private scheduleNext(): void {
    const scalar = this.currentRampScalar();
    if (this.burstRemaining > 0) {
      this.burstRemaining -= 1;
      this.nextSpawnAtMs += 1500 + this.opts.rng() * 1500;
    } else {
      // Start a new burst of 1-3 events with a long gap before it.
      this.burstRemaining = Math.floor(this.opts.rng() * 3);
      const baseGap = this.opts.intervalMs * scalar;
      this.nextSpawnAtMs += baseGap + this.opts.rng() * (baseGap * 0.5);
    }
  }

  private spawn(spawnAtMs: number): void {
    const kind = this.pickKind();
    if (kind === 'email') return this.spawnEmail(spawnAtMs);
    if (kind === 'aging-tick') return this.spawnAgingTick();
    if (kind === 'call') return this.spawnCall();
    if (kind === 'payment') return this.spawnPayment(spawnAtMs);
  }

  private pickKind(): EventKind {
    const allowAging = this.opts.unrestricted || (this.opts.agingUnlocked?.() ?? true);
    const allowCalls = this.opts.unrestricted || (this.opts.callsUnlocked?.() ?? false);
    const allowPayments = this.opts.unrestricted || (this.opts.paymentsUnlocked?.() ?? false);
    // Always allow emails. Per-kind rate multipliers come from the
    // settings modal — 0 silences a kind, 1 is the base share.
    const weights: Record<EventKind, number> = {
      email: BASE_SHARES.email * this.rates.email,
      'aging-tick': allowAging ? BASE_SHARES['aging-tick'] * this.rates['aging-tick'] : 0,
      payment: allowPayments ? BASE_SHARES.payment * this.rates.payment : 0,
      call: allowCalls ? BASE_SHARES.call * this.rates.call : 0,
    };
    const total = weights.email + weights['aging-tick'] + weights.payment + weights.call;
    if (total <= 0) return 'email';
    const r = this.opts.rng() * total;
    if (r < weights.email) return 'email';
    if (r < weights.email + weights['aging-tick']) return 'aging-tick';
    if (r < weights.email + weights['aging-tick'] + weights.payment) return 'payment';
    return 'call';
  }

  private spawnEmail(spawnAtMs: number): void {
    const pool = this.opts.pool;
    if (pool.length === 0) return;
    const idx = Math.floor(this.opts.rng() * pool.length);
    const template = pool[Math.min(idx, pool.length - 1)];
    this.idCounter += 1;
    const email: Email = {
      id: `proc-${this.idCounter}`,
      subject: template.subject,
      from: template.from,
      body: template.body,
      category: template.category,
      receivedAt: this.opts.formatTimeLabel ? this.opts.formatTimeLabel(spawnAtMs) : '',
      read: false,
      ...(template.replyChallenge ? { replyChallenge: template.replyChallenge } : {}),
    };
    this.opts.bus.emit('email:received', { email });
  }

  private spawnAgingTick(): void {
    const customer = PROCEDURAL_AGING_CUSTOMERS[
      Math.floor(this.opts.rng() * PROCEDURAL_AGING_CUSTOMERS.length)
    ];
    // Pick a random "from" bucket and step it forward.
    const fromIdx = Math.floor(this.opts.rng() * (BUCKETS.length - 1));
    const fromBucket: Bucket = BUCKETS[fromIdx];
    const toBucket: Bucket = BUCKETS[fromIdx + 1];
    const amount = Math.round(2000 + this.opts.rng() * 8000);
    this.opts.bus.emit('aging-tick:fired', { customer, fromBucket, toBucket, amount });
  }

  private spawnCall(): void {
    const caller = PROCEDURAL_CALL_CALLERS[
      Math.floor(this.opts.rng() * PROCEDURAL_CALL_CALLERS.length)
    ];
    this.opts.bus.emit('procedural:call-rang', { caller });
  }

  private spawnPayment(spawnAtMs: number): void {
    if (PAYMENT_ELIGIBLE_CUSTOMERS.length === 0) return;
    const customer = PAYMENT_ELIGIBLE_CUSTOMERS[
      Math.floor(this.opts.rng() * PAYMENT_ELIGIBLE_CUSTOMERS.length)
    ];
    const invoices = openInvoicesForCustomer(customer);
    if (invoices.length === 0) return;
    // 70% chance: amount = sum of 1–3 of their invoices (clean apply).
    // 30% chance: a partial amount (still treated as standard cash app for v1).
    let amount: number;
    if (this.opts.rng() < 0.7) {
      const k = 1 + Math.floor(this.opts.rng() * Math.min(3, invoices.length));
      const picked = invoices.slice(0, k);
      amount = picked.reduce((s, i) => s + i.amount, 0);
    } else {
      const inv = invoices[Math.floor(this.opts.rng() * invoices.length)];
      amount = Math.round(inv.amount * (0.5 + this.opts.rng() * 0.4));
    }
    this.idCounter += 1;
    const wireId = `W-${3000 + this.idCounter}`;
    const payment: PendingPayment = {
      id: wireId,
      customer,
      amount,
      remittance: buildRemittanceHint(customer, this.opts.rng),
      arrivedAtMs: spawnAtMs,
      expiresAtMs: spawnAtMs + PAYMENT_EXPIRATION_MS,
    };
    this.opts.bus.emit('payment:queued', { payment });
  }
}
